/**
 * Partners API Module
 * Cộng tác viên (CTV) bán hàng + bảng giá sỉ theo từng CTV.
 *
 * CTV bán trên site CMS riêng của họ rồi đẩy đơn về CMS tổng này. Giá sỉ là con số
 * ghi công nợ CTV (giá sỉ × số lượng), không liên quan tới giá CTV bán cho khách cuối.
 *
 * Toàn bộ endpoint dưới đây CHỈ ADMIN (backend: require_admin).
 */

import apiClient, { extractData } from '../api-client';

export const partnerApi = {
  /**
   * Danh sách CTV (phân trang server-side).
   *
   * KHÔNG kèm api_key/webhook_secret — hai field đó luôn null ở đây, phải gọi
   * `getById` mới có. Đừng dựng màn hình "xem khoá" từ dữ liệu của list.
   *
   * @param {Object} params
   * @param {number} params.page - Trang (mặc định 1)
   * @param {number} params.size - Số dòng mỗi trang (mặc định 20, tối đa 100)
   * @param {boolean|null} params.is_active - Lọc theo trạng thái hợp tác
   * @returns {Promise<Object>} PaginatedResponse<Partner>
   */
  list(params) {
    return apiClient.get('/partners', { params }).then(extractData);
  },

  /**
   * Chi tiết một CTV, KÈM api_key + webhook_secret để dán vào site CTV.
   * @param {number} partnerId
   * @returns {Promise<Object>} Partner
   */
  getById(partnerId) {
    return apiClient.get(`/partners/${partnerId}`).then(extractData);
  },

  /**
   * Thêm CTV mới. Response có kèm khoá (lần duy nhất khoá tự hiện ra mà không phải
   * mở màn hình xem khoá).
   *
   * @param {Object} data
   * @param {string} data.name - Tên CTV (bắt buộc)
   * @param {string} data.domain - Domain site CTV (bắt buộc, backend tự bỏ https:// và path)
   * @param {string} [data.contact_phone]
   * @param {string} [data.notes]
   * @param {string} [data.webhook_url]
   * @returns {Promise<Object>} Partner có kèm khoá
   */
  create(data) {
    return apiClient.post('/partners', data).then(extractData);
  },

  /**
   * Sửa thông tin CTV.
   * @param {number} partnerId
   * @param {Object} data - name / domain / contact_phone / notes / webhook_url / is_active
   * @returns {Promise<Object>} Partner (KHÔNG kèm khoá)
   */
  update(partnerId, data) {
    return apiClient.put(`/partners/${partnerId}`, data).then(extractData);
  },

  /**
   * Sinh lại api_key + webhook_secret.
   *
   * Khoá cũ chết ngay: site CTV sẽ bị 401 từ lần đẩy đơn kế tiếp cho tới khi dán
   * khoá mới — giao diện phải hỏi xác nhận trước khi gọi.
   *
   * @param {number} partnerId
   * @returns {Promise<Object>} Partner kèm khoá MỚI
   */
  regenerateKeys(partnerId) {
    return apiClient.post(`/partners/${partnerId}/regenerate-keys`).then(extractData);
  },

  /**
   * Link một lần để super admin vào thẳng CMS của CTV (tài khoản ẩn "Super admin tổng").
   *
   * CHỈ super_admin (backend 403 với mọi role khác). Vé trong link sống 60 giây và
   * dùng được đúng một lần — gọi ngay trước khi mở tab, đừng lưu lại.
   *
   * @param {number} partnerId
   * @returns {Promise<{url: string}>}
   */
  ssoTicket(partnerId) {
    return apiClient.post(`/partners/${partnerId}/sso-ticket`).then(extractData);
  },

  /**
   * Ngừng hợp tác = soft delete (is_active=false), KHÔNG xoá bản ghi — đơn và sổ
   * công nợ cũ của CTV phải giữ lại để đối soát.
   *
   * @param {number} partnerId
   * @returns {Promise<Object>} Message response
   */
  deactivate(partnerId) {
    return apiClient.delete(`/partners/${partnerId}`).then(extractData);
  },

  /**
   * Bảng giá sỉ của một sản phẩm: giá bán lẻ + giá sỉ mặc định + giá riêng từng CTV.
   *
   * Trả MỌI CTV đang hoạt động; `price === null` nghĩa là CTV đó chưa đặt giá riêng
   * và đang dùng giá mặc định.
   *
   * @param {number} productId
   * @returns {Promise<Object>} ProductPricesResponse
   */
  getProductPrices(productId) {
    return apiClient.get(`/partners/prices/${productId}`).then(extractData);
  },

  /**
   * Đặt / xoá MỘT dòng giá sỉ. Trả lại nguyên bảng giá sau khi lưu.
   *
   * @param {number} productId
   * @param {Object} data
   * @param {number|null} [data.partner_id] - Bỏ trống = đặt giá MẶC ĐỊNH cho mọi CTV
   * @param {number|null} [data.price] - Bỏ trống = XOÁ dòng đó (CTV quay về giá mặc định)
   * @returns {Promise<Object>} ProductPricesResponse
   */
  setProductPrice(productId, data) {
    return apiClient.put(`/partners/prices/${productId}`, data).then(extractData);
  },

  /**
   * Bảng giá sỉ của MỘT CTV: mọi sản phẩm đang bán, xếp theo mã.
   *
   * Mỗi dòng: retail_price, default_price, partner_price (null = chưa có giá riêng),
   * effective_price đã giải ba nấc (riêng → mặc định → niêm yết) và `source` là
   * "partner" | "default" | "retail".
   *
   * @param {number} partnerId
   * @returns {Promise<Object>} PartnerPriceTableResponse
   */
  getPartnerPrices(partnerId) {
    return apiClient.get(`/partners/${partnerId}/prices`).then(extractData);
  },

  /**
   * Lưu hàng loạt giá riêng của một CTV — tất cả hoặc không dòng nào (tối đa 500).
   * Trả lại nguyên bảng sau khi lưu.
   *
   * @param {number} partnerId
   * @param {Array<{product_id: number, price: number|null}>} items - price null = bỏ giá riêng
   * @returns {Promise<Object>} PartnerPriceTableResponse
   */
  setPartnerPrices(partnerId, items) {
    return apiClient.put(`/partners/${partnerId}/prices`, { items }).then(extractData);
  }
};
